import { Component } from '@theme/component';

/**
 * Free-entry dimension fields for made-to-measure products.
 *
 * Three jobs:
 *   1. Keep each field's readout in step with what is typed - the current
 *      value ("150cm") normally, or the breached limit ("Max 200cm") when
 *      the value is out of range.
 *   2. Pass the entered values to the cart as line item properties.
 *      The inputs render outside the <form> (buy-buttons owns it), so we
 *      inject hidden mirror inputs directly inside the form and keep them
 *      in sync. This is more reliable than the HTML `form` attribute, which
 *      only works when the form has an id.
 *   3. Refuse to submit while any value is out of range.
 */
class ProductCustomSize extends Component {
  #form = null;

  connectedCallback() {
    super.connectedCallback();

    // The block is rendered on every PDP but only made-to-measure products
    // switch it on (data-enabled, from blocks/product-custom-size.liquid).
    // Elsewhere it is display:none - and must also stay inert, otherwise
    // #updateSubmitButton sees the empty dimension fields and disables the
    // add-to-cart button on ordinary in-stock products.
    if (this.dataset.enabled !== 'true') return;

    this.#form = this.#findProductForm();

    // Inject hidden mirror inputs inside the actual <form> so the values
    // are included in the form submission regardless of `form` attribute support.
    this.#injectMirrorInputs();

    // Bubble-phase on the form itself, so this runs before the delegated
    // document-level listener that drives <product-form-component>.
    this.#form?.addEventListener('submit', this.handleSubmit);
    this.addEventListener('input', this.handleInput);

    for (const field of this.#fields) this.#refresh(field);
    this.#updateSubmitButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#form?.removeEventListener('submit', this.handleSubmit);
    this.removeEventListener('input', this.handleInput);
    this.#removeMirrorInputs();
  }

  /** @returns {HTMLElement[]} */
  get #fields() {
    return Array.from(this.querySelectorAll('[data-custom-size-field]'));
  }

  /** @returns {HTMLInputElement[]} */
  get #inputs() {
    return Array.from(this.querySelectorAll('[data-custom-size-input]'));
  }

  /**
   * Finds the add-to-cart form for this product.
   * @returns {HTMLFormElement | null}
   */
  #findProductForm() {
    const scope = this.closest('.shopify-section, dialog') ?? document;
    return scope.querySelector('form[data-type="add-to-cart-form"]');
  }

  /**
   * Creates a hidden <input type="hidden"> inside the product form for each
   * visible dimension input. The name is the same properties[…] name so
   * Shopify picks it up as a line item property.
   */
  #injectMirrorInputs() {
    if (!this.#form) return;

    for (const input of this.#inputs) {
      // Avoid duplicates (e.g. after a Shopify section reload).
      const existing = this.#form.querySelector(
        `[data-custom-size-mirror="${CSS.escape(input.name)}"]`
      );
      if (existing) continue;

      const mirror = document.createElement('input');
      mirror.type = 'hidden';
      mirror.name = input.name;
      mirror.value = input.value;
      // Tag with the property name so we can find and update it later.
      mirror.dataset.customSizeMirror = input.name;
      this.#form.appendChild(mirror);
    }
  }

  /**
   * Syncs mirror input values with the visible inputs.
   */
  #syncMirrorInputs() {
    if (!this.#form) return;

    for (const input of this.#inputs) {
      const mirror = this.#form.querySelector(
        `[data-custom-size-mirror="${CSS.escape(input.name)}"]`
      );
      if (mirror instanceof HTMLInputElement) {
        mirror.value = input.value;
      }
    }
  }

  /**
   * Removes mirror inputs when this element is removed from the DOM.
   */
  #removeMirrorInputs() {
    if (!this.#form) return;

    for (const mirror of this.#form.querySelectorAll('[data-custom-size-mirror]')) {
      mirror.remove();
    }
  }

  /**
   * Disables or enables the form's submit button based on validity.
   */
  #updateSubmitButton() {
    if (!this.#form) return;
    
    // Find the add to cart / submit button
    const submitBtn = this.#form.querySelector('button[type="submit"], input[type="submit"]');
    if (!submitBtn) return;
    
    const isInvalid = this.#fields.some((field) => {
      const input = field.querySelector('[data-custom-size-input]');
      const isEmpty = input instanceof HTMLInputElement && input.value.trim() === '';
      const isZero = input instanceof HTMLInputElement && Number(input.value) === 0;
      return field.dataset.invalid === 'true' || isEmpty || isZero;
    });
    
    // Disable or enable the button based on the invalid state
    if (isInvalid) {
      submitBtn.disabled = true;
    } else {
      submitBtn.disabled = false;
    }
  }

  /** @param {Event} event */
  handleInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-custom-size-input')) return;

    const field = input.closest('[data-custom-size-field]');
    if (field instanceof HTMLElement) this.#refresh(field);

    // Keep mirror inputs in sync so the form always submits the latest value.
    this.#syncMirrorInputs();
    
    this.#updateSubmitButton();
  };

  /** @param {Event} event */
  handleSubmit = (event) => {
    // Sync once more right before submit in case the user didn't fire an
    // input event (e.g. browser autofill).
    this.#syncMirrorInputs();

    const invalid = this.#fields.filter((field) => this.#refresh(field));
    if (invalid.length === 0) return;

    event.preventDefault();
    event.stopPropagation();
    invalid[0]?.querySelector('input')?.focus();
  };

  /**
   * Recomputes a field's validity and rewrites its readout.
   * @param {HTMLElement} field
   * @returns {boolean} True when the field is out of range.
   */
  #refresh(field) {
    const input = field.querySelector('[data-custom-size-input]');
    const readout = field.querySelector('[data-custom-size-readout]');
    if (!(input instanceof HTMLInputElement) || !readout) return false;

    const unit = this.dataset.unit ?? '';
    const min = Number(field.dataset.min);
    const max = Number(field.dataset.max);
    const value = Number(input.value);

    let invalid = false;
    let text = `${input.value === '' ? 0 : value}${unit}`;

    if (input.value !== '' && Number.isFinite(max) && value > max) {
      invalid = true;
      text = this.#limitLabel('max', max, unit);
    } else if (input.value !== '' && Number.isFinite(min) && value < min) {
      invalid = true;
      text = this.#limitLabel('min', min, unit);
    }

    readout.textContent = text;
    field.dataset.invalid = String(invalid);
    input.setAttribute('aria-invalid', String(invalid));

    return invalid;
  }

  /**
   * Builds "Max 200cm" / "Min 10cm" from the localized template on the host.
   * @param {'max' | 'min'} kind
   * @param {number} value
   * @param {string} unit
   * @returns {string}
   */
  #limitLabel(kind, value, unit) {
    const template = kind === 'max' ? this.dataset.maxTemplate : this.dataset.minTemplate;
    if (!template) return `${value}${unit}`;
    return template.replace('[value]', String(value)).replace('[unit]', unit);
  }
}

if (!customElements.get('product-custom-size')) {
  customElements.define('product-custom-size', ProductCustomSize);
} 
